import { trackMediaListening } from '@/utils/listeningActivity'
import { ref, unref, watch } from 'vue'
import { VAD_SAMPLE_RATE, combineFloat32Arrays, float32ToWavBlob } from '@/utils/audio/wav'

const RECORDING_TTL_MS = 24 * 60 * 60 * 1000
const DB_NAME = 'goodscriber-practice-recordings'
const STORE_NAME = 'recordings'
const DB_VERSION = 1
const OUTPUT_SAMPLE_RATE = 48000

function openDatabase() {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null)
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.addEventListener('upgradeneeded', () => {
      const database = request.result
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME, { keyPath: 'id' })
      }
    })
    request.addEventListener('success', () => resolve(request.result))
    request.addEventListener('error', () => reject(request.error))
  })
}

async function withStore(mode, operation) {
  const database = await openDatabase()
  if (!database) return null
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, mode)
    const store = transaction.objectStore(STORE_NAME)
    const request = operation(store)
    transaction.addEventListener('complete', () => {
      database.close()
      resolve(request?.result ?? null)
    })
    transaction.addEventListener('error', () => {
      database.close()
      reject(transaction.error)
    })
  })
}

function sessionId(value) {
  return `audio:${String(value || 'unknown')}`
}

function recordingId(session, index) {
  return `${session}:${Number(index)}`
}

async function persistRecording(session, index, blob, duration) {
  const now = Date.now()
  await withStore('readwrite', (store) => store.put({
    id: recordingId(session, index),
    session,
    index: Number(index),
    blob,
    duration,
    updatedAt: now,
    expiresAt: now + RECORDING_TTL_MS,
  }))
}

async function loadSessionRecordings(session) {
  const rows = await withStore('readonly', (store) => store.getAll()) || []
  const now = Date.now()
  const expiredIds = rows.filter((row) => row.expiresAt <= now).map((row) => row.id)
  if (expiredIds.length) {
    await withStore('readwrite', (store) => {
      expiredIds.forEach((id) => store.delete(id))
      return null
    })
  }
  return rows.filter((row) => row.session === session && row.expiresAt > now && row.blob)
}

function downmixAudioBuffer(buffer) {
  const output = new Float32Array(buffer.length)
  for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
    const input = buffer.getChannelData(channel)
    for (let i = 0; i < input.length; i++) output[i] += input[i] / buffer.numberOfChannels
  }
  return output
}

function resampleLinear(samples, sourceRate, targetRate) {
  if (sourceRate === targetRate) return samples
  const targetLength = Math.max(1, Math.round(samples.length * targetRate / sourceRate))
  const output = new Float32Array(targetLength)
  const ratio = sourceRate / targetRate
  for (let i = 0; i < targetLength; i++) {
    const position = i * ratio
    const left = Math.floor(position)
    const right = Math.min(left + 1, samples.length - 1)
    const mix = position - left
    output[i] = samples[left] * (1 - mix) + samples[right] * mix
  }
  return output
}

export function useRecordingLibrary({ sessionKey } = {}) {
  const recordings = ref(new Map())
  const recordingDurations = ref(new Map())
  const totalRecordingTime = ref(0)
  const currentRecordingTime = ref(0)
  const currentRecordingAudio = ref(null)
  const isPlayingRecordings = ref(false)
  const playingRecordingIndex = ref(null)
  let playbackRequestId = 0
  let restoreGeneration = 0
  let persistenceQueue = Promise.resolve()

  function updateTotalRecordingTime() {
    totalRecordingTime.value = Array.from(recordingDurations.value.values())
      .reduce((total, duration) => total + duration, 0)
  }

  async function restoreRecordings() {
    const generation = ++restoreGeneration
    const session = sessionId(unref(sessionKey))
    try {
      const rows = await loadSessionRecordings(session)
      if (generation !== restoreGeneration || session !== sessionId(unref(sessionKey))) return
      const restoredRecordings = new Map(rows.map((row) => [row.index, row.blob]))
      const restoredDurations = new Map(rows.map((row) => [row.index, row.duration]))
      recordings.value.forEach((blob, index) => restoredRecordings.set(index, blob))
      recordingDurations.value.forEach((duration, index) => restoredDurations.set(index, duration))
      recordings.value = restoredRecordings
      recordingDurations.value = restoredDurations
      updateTotalRecordingTime()
    } catch {
      // IndexedDB may be unavailable in private browsing. In-memory recording still works.
    }
  }

  function saveRecording(index, assessmentAudio, originalRecording = null) {
    const assessmentCopy = new Float32Array(assessmentAudio)
    const assessmentBlob = float32ToWavBlob(assessmentCopy, VAD_SAMPLE_RATE)
    const assessmentDuration = assessmentCopy.length / VAD_SAMPLE_RATE
    const playbackBlob = originalRecording?.blob || assessmentBlob
    const duration = Number(originalRecording?.duration) || assessmentDuration

    recordings.value.set(index, playbackBlob)
    recordingDurations.value.set(index, duration)
    updateTotalRecordingTime()
    const session = sessionId(unref(sessionKey))
    persistenceQueue = persistenceQueue
      .then(() => persistRecording(session, index, playbackBlob, duration))
      .catch(() => {})
    return { blob: assessmentBlob, duration }
  }

  async function buildCombinedRecordingBlob() {
    const entries = Array.from(recordings.value.entries()).sort(([a], [b]) => a - b)
    if (!entries.length) return null
    const AudioContextClass = window.AudioContext || window.webkitAudioContext
    if (!AudioContextClass) return null
    const context = new AudioContextClass()
    try {
      const chunks = []
      for (const [, blob] of entries) {
        const buffer = await context.decodeAudioData(await blob.arrayBuffer())
        chunks.push(resampleLinear(downmixAudioBuffer(buffer), buffer.sampleRate, OUTPUT_SAMPLE_RATE))
      }
      return float32ToWavBlob(combineFloat32Arrays(chunks), OUTPUT_SAMPLE_RATE)
    } finally {
      await context.close().catch(() => {})
    }
  }

  async function playAllRecordings() {
    if (isPlayingRecordings.value) {
      stopPlayingRecordings()
      return
    }
    const requestId = ++playbackRequestId
    isPlayingRecordings.value = true
    const blob = await buildCombinedRecordingBlob().catch(() => null)
    if (!blob || requestId !== playbackRequestId) {
      if (requestId === playbackRequestId) isPlayingRecordings.value = false
      return
    }
    playBlob(blob)
  }

  function playRecording(index) {
    const blob = recordings.value.get(index)
    if (!blob) return
    if (isPlayingRecordings.value && playingRecordingIndex.value === index) {
      stopPlayingRecordings()
      return
    }
    stopPlayingRecordings()
    playingRecordingIndex.value = index
    playBlob(blob)
  }

  function playBlob(blob) {
    const url = URL.createObjectURL(blob)
    const audio = new Audio(url)
    trackMediaListening(audio)
    currentRecordingAudio.value = audio
    isPlayingRecordings.value = true
    currentRecordingTime.value = 0
    audio.addEventListener('timeupdate', () => {
      currentRecordingTime.value = audio.currentTime
    })
    audio.addEventListener('ended', stopPlayingRecordings)
    audio.play().catch(() => stopPlayingRecordings())
  }

  function stopPlayingRecordings() {
    playbackRequestId++
    if (currentRecordingAudio.value) {
      currentRecordingAudio.value.pause()
      URL.revokeObjectURL(currentRecordingAudio.value.src)
      currentRecordingAudio.value = null
    }
    isPlayingRecordings.value = false
    playingRecordingIndex.value = null
    currentRecordingTime.value = 0
  }

  async function downloadRecordings(fileName = 'my-shadowing-recording', suffix = 'shadowing-recording') {
    const blob = await buildCombinedRecordingBlob().catch(() => null)
    if (!blob) return
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const safeName = String(fileName || 'my-shadowing-recording').replace(/\.[^/.]+$/, '').replace(/[\\/:*?"<>|]+/g, '-').trim()
    const safeSuffix = String(suffix || 'shadowing-recording').replace(/[\\/:*?"<>|]+/g, '-').trim()
    a.download = `${safeName || 'my-shadowing-recording'}-${safeSuffix || 'shadowing-recording'}.wav`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  function clearMemory() {
    recordings.value = new Map()
    recordingDurations.value = new Map()
    totalRecordingTime.value = 0
  }

  watch(() => unref(sessionKey), () => {
    stopPlayingRecordings()
    clearMemory()
    void restoreRecordings()
  })
  void restoreRecordings()

  return {
    recordings,
    recordingDurations,
    totalRecordingTime,
    currentRecordingTime,
    isPlayingRecordings,
    playingRecordingIndex,
    saveRecording,
    playRecording,
    playAllRecordings,
    stopPlayingRecordings,
    downloadRecordings,
  }
}
